import { languageDisplayLabel, LanguagesEnum } from '@/api/enums';
import { Course } from '@/client/courses';
import { aiCustomizationLanguages } from '@/common/constants/languages';
import { sortAlphabetically } from '@/utils/helpers';
import { preventDropdownBrowserTranslate } from '@/utils/helpers/ui.helper';
import { Dropdown, DropdownChangeEvent } from 'primereact/dropdown';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const StyledDropdown = styled(Dropdown)`
  border-radius: var(--default-border-radius);
  background: var(--white-main);
  width: 450px;
  max-width: 100%;

  .p-dropdown-label.p-placeholder,
  .p-dropdown-label.p-inputtext {
    background: var(--white-main);
  }

  .p-dropdown-label.p-inputtext {
    padding-right: 0px;
  }
`;

type LanguageOption = {
  id: LanguagesEnum;
  label: string;
  disabled: boolean;
};

type CourseCustomizationLanguageSelectorProps = {
  course: Course;
  onChange: (value: LanguagesEnum) => void;
  selectedLanguage?: LanguagesEnum;
  isDisabled?: boolean;
  className?: string;
};

export const CourseCustomizationLanguageSelector: React.FC<
  CourseCustomizationLanguageSelectorProps
> = ({ course, onChange, selectedLanguage, isDisabled, className }) => {
  const { t } = useTranslation();
  const [languageOptions, setLanguageOptions] = useState<LanguageOption[]>([]);

  useEffect(() => {
    // Offer every customization language, disabling the ones this course has no version of
    const options = aiCustomizationLanguages.map((language) => ({
      id: language,
      label: languageDisplayLabel[language],
      disabled: !course.courseLanguages.includes(language),
    }));

    setLanguageOptions(
      options.sort((a, b) => sortAlphabetically(a.label, b.label)),
    );
  }, [course]);

  const onChangeHandler = (e: DropdownChangeEvent) => {
    onChange(e.value);
  };

  // If the course has no customizable language at all, don't render the dropdown
  if (!languageOptions.some((option) => !option.disabled)) {
    return null;
  }

  return (
    <label>
      <StyledDropdown
        id="customization-language"
        placeholder={t('generic.select')}
        value={selectedLanguage}
        optionValue="id"
        optionLabel="label"
        optionDisabled={(option: LanguageOption) => option.disabled}
        onChange={onChangeHandler}
        options={languageOptions}
        className={className}
        disabled={isDisabled}
        filter
        filterInputAutoFocus
        {...preventDropdownBrowserTranslate}
      />
    </label>
  );
};
